import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import LoadingSpinner from './loading-spinner';
import Popover from './popover';
import { ReactComponent as DeletedUserAvatar } from '../../images/deleted-user-avatar.svg';
import Icon, { COLORS } from './icon';
import { getFullAssetURI, getNameOrInitials } from '../../utils/utils';

export const brightColors = [
	'#026A93',
	'#EF731A',
	'#28374E',
	'#576377',
	'#00A4E4',
	'#8B939F',
	'#62B45C',
	'#42B590',
	'#0084B8',
	'#D3112C',
	'#3c5a99',
	'#1da1f2',
	'#f02261',
	'#9B9B9B',
];

export const pickColor = (char: string) => {
	if (char) {
		return brightColors[char.charCodeAt(0) % 13];
	}
	return brightColors[0];
};

export interface AvatarUser {
	first_name?: string;
	last_name?: string;
	email: string;
	avatar?: string | null;
	color?: string;
	is_deleted?: boolean;
	icon?: string;
	type?: string;
}

interface AvatarProps {
	user: AvatarUser;
	loading?: boolean;
	size?: string;
	noBorder?: boolean;
	selected?: boolean;
	host?: boolean;
	inMeeting?: boolean;
	onClick?: (e: any) => void;
	children?: any;
	popoverExtraTopMargin?: boolean;
	cursorPointer?: boolean;
	setAvatarPopoverOpen?: Function;
	avatarPopoverOpen?: boolean | false;
	style?: React.CSSProperties;
}

function getBackgroundColor(user: AvatarUser) {
	return { backgroundColor: user.color ? user.color : pickColor(user.email) };
}

function getThumb(user: AvatarUser) {
	if (user) {
		if (user.icon) {
			return (
				<div className={`audio-avatar ${user.type}`} data-testid="avatar-bubble">
					<Icon name={user.icon} color={COLORS.WHITE} size={14}></Icon>
				</div>
			);
		}
		if (user.is_deleted) {
			return <DeletedUserAvatar />;
		}
		if (user.avatar) {
			const url = user.avatar.includes('https://')
				? user.avatar
				: getFullAssetURI(user.avatar);
			return (
				<img src={url} alt={'user avatar'} style={getBackgroundColor(user)} data-testid="avatar-bubble" />
			);
		}
		return (
				<div className="avatar-text" style={getBackgroundColor(user)} data-testid="avatar-bubble">
					{getNameOrInitials(user, false)}
				</div>
		);
	}
	return <></>;
}

export default function Avatar(props: AvatarProps) {
	const {
		user,
		loading = false,
		size = 'normal',
		noBorder = false,
		selected = false,
		host = false,
		inMeeting = false,
		setAvatarPopoverOpen,
		avatarPopoverOpen,
		style = {}
	} = props;

	const [open, setOpen] = useState(false);

	function toggleOpen() {
		if (avatarPopoverOpen === undefined) {
			setOpen(!open);
		}
	}

	function deadClick() {

	}

	function handleClick(e: any) {
		if (e && e.composedPath) {
			let keepOpen = false;
			const path = e.composedPath();
			const len = path.length;
			for (let i = 0; i < len; i += 1) {
				if (typeof path[i].className === 'string' && path[i].className.includes('avatar-button')) {
					keepOpen = true;
					break;
				}
			}
			// eslint-disable-next-line no-unused-expressions
			if (!keepOpen) setAvatarPopoverOpen ? setAvatarPopoverOpen(false) : setOpen(false);
		}
	}

	useEffect(() => {
		if (avatarPopoverOpen) {
			setOpen(true);
			setTimeout(() => {
				window.addEventListener('click', handleClick);
			});
		} else if (avatarPopoverOpen !== undefined) {
			window.removeEventListener('click', handleClick);
			setTimeout(() => setOpen(false));
		}
		return () => window.removeEventListener('click', handleClick);
	}, [avatarPopoverOpen]);

	return (
		<div className={classNames(
			'avatar',
			size,
			{
				'no-border': noBorder,
				selected,
				host,
				'in-meeting': inMeeting,
			},
		)}
			style={{ 
				...style,
				cursor: !!props.children || props.cursorPointer ? 'pointer' : 'auto'
			}}
			onClick={props.children ? toggleOpen : deadClick}
		>
			{getThumb(user)}
			{loading && (
				<div className="loading-background">
					<LoadingSpinner
						height={20}
						width={20}
						stroke={10}
					/>
				</div>
			)}
			{props.children && (
				<Popover
					open={open}
					close={toggleOpen}
					popoverExtraTopMargin={props.popoverExtraTopMargin}
				>
					{props.children}
				</Popover>
			)}
		</div>
	);
}
